import { useEffect } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isValidLanguage } from '../utils/languageUtils';
import { defaultLang } from '../config';
import App from '../App';

// ----------------------------------------------------------------------

// A first segment that looks like a language tag ("fr", "pt-BR") but isn't one
// we support. Those get replaced; anything else is a real path and is kept.
const LANG_LIKE = /^[a-z]{2}(-[a-zA-Z]{2})?$/;

/**
 * Turn a path that failed language validation into the same page under the
 * default language.
 *
 * Two very different cases arrive here:
 * - "/privacy" — a legacy URL with no language prefix at all. The whole path
 *   is content and must survive: "/es/privacy".
 * - "/fr/blog" — a real prefix for a language we don't serve. Swap it:
 *   "/es/blog".
 *
 * Dropping the path (what this used to do) sent every unprefixed URL to the
 * home page, including the privacy policy and terms links declared in Google
 * Play and opened from the mobile app.
 */
export function localizedFallbackPath(pathname, lang = defaultLang.value) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && LANG_LIKE.test(parts[0])) {
    parts.shift();
  }
  return `/${[lang, ...parts].join('/')}`;
}

/**
 * LanguageAwareApp Component
 *
 * Validates language parameter from URL and syncs with i18next
 * Wraps the main App component to ensure language consistency
 *
 * Responsibilities:
 * 1. Validate language parameter from URL
 * 2. Redirect to default language if invalid, keeping the requested page
 * 3. Sync i18next with URL language (without page reload)
 * 4. Update localStorage for persistence
 * 5. Render main App component
 *
 * @component
 * @returns {JSX.Element} Main App or redirect to valid language
 */
export default function LanguageAwareApp() {
  const { lang } = useParams();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const isValidLang = isValidLanguage(lang);

  // Sync i18next with URL language
  useEffect(() => {
    if (!isValidLang) {
      return;
    }

    // Change language without reloading the page
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }

    // The language of the page being viewed. i18next no longer caches it,
    // so it is written on every page: axios reads 'language'.
    localStorage.setItem('i18nextLng', lang);
    localStorage.setItem('language', lang);
  }, [isValidLang, lang, i18n]);

  // Validate language parameter
  if (!isValidLang) {
    // Same page, default language — not the home page.
    return <Navigate to={localizedFallbackPath(pathname)} replace />;
  }

  // Render main App component
  return <App />;
}
